//! Formula language services for the custom cell editor. These wrap the
//! quadratic-core WASM exports that were originally designed for Monaco
//! (`provideCompletionItems`, `provideHover`) and expose plain, Monaco-free
//! data structures so the formula bar can render its own autocomplete and
//! function-hint UI.

import { provideCompletionItems, provideHover } from '@/app/quadratic-core/quadratic_core';

// Section headers shown in the autocomplete dropdown. Items are grouped by
// section in list order; empty sections are simply absent from the item list.
export const COMPLETION_SECTION_RECENT = 'Recent functions';
export const COMPLETION_SECTION_FUNCTIONS = 'Functions';
export const COMPLETION_SECTION_NAMED_RANGES = 'Named ranges';

export interface FormulaCompletion {
  label: string;
  detail: string;
  documentation: string;
  // Snippet-style insert text from the LSP, e.g. "SUM(${1:numbers})".
  insertText: string;
  kind: 'function' | 'definedName';
  section: string;
}

interface WasmDocumentation {
  value: string;
}

interface WasmCompletionItem {
  label: string;
  detail?: string | null;
  documentation?: WasmDocumentation | string | null;
  insertText?: string | null;
}

interface WasmCompletionList {
  suggestions: WasmCompletionItem[];
}

interface WasmHover {
  contents: WasmDocumentation[];
}

const documentationToString = (documentation: WasmCompletionItem['documentation']): string => {
  if (!documentation) return '';
  if (typeof documentation === 'string') return documentation;
  return documentation.value;
};

let cachedCompletions: FormulaCompletion[] | undefined;

/// Returns the full list of formula function completions. The WASM export
/// ignores position/context and always returns every function, so we cache the
/// transformed result.
export const getFormulaCompletions = (): FormulaCompletion[] => {
  if (cachedCompletions) return cachedCompletions;
  try {
    const result = provideCompletionItems(undefined, undefined, undefined, undefined) as unknown as WasmCompletionList;
    cachedCompletions = (result?.suggestions ?? []).map((item) => ({
      label: item.label,
      detail: item.detail ?? '',
      documentation: documentationToString(item.documentation),
      insertText: item.insertText ?? `${item.label}()`,
      kind: 'function' as const,
      section: COMPLETION_SECTION_FUNCTIONS,
    }));
  } catch (_e) {
    cachedCompletions = [];
  }
  return cachedCompletions;
};

/// Returns markdown documentation for the function with the given name (or
/// undefined if there is no matching function). Uses a minimal shim object in
/// place of the Monaco text model the WASM export expects.
export const getFunctionHover = (functionName: string): string | undefined => {
  if (!functionName) return undefined;
  try {
    const textModelShim = { getWordAtPosition: () => ({ word: functionName }) };
    const result = provideHover(textModelShim, undefined, undefined) as unknown as WasmHover | undefined | null;
    if (!result || !result.contents?.length) return undefined;
    return result.contents.map((c) => c.value).join('\n');
  } catch (_e) {
    return undefined;
  }
};

/// Converts an LSP snippet (e.g. "IF(${1:condition}, ${2:t}, ${3:f})") into the
/// plain text to insert plus the caret offset (relative to the inserted text)
/// where the cursor should land. For functions we drop the placeholders and
/// place the caret after the opening parenthesis.
export const snippetToInsert = (
  snippet: string,
  label: string,
  nextChar?: string
): { text: string; caretOffset: number } => {
  // If the snippet opens a parenthesis, insert only "<label>(" (no closing
  // parenthesis) so the user can immediately type arguments. The closing
  // parenthesis is added automatically on commit (matching Excel/Sheets, which
  // avoids confusing non-coders with a dangling ")").
  if (snippet.includes('(')) {
    // When replacing the name of an existing call (e.g. SUM(…) → AVERAGE(…)),
    // the "(" already follows the insertion point, so insert only the label and
    // move the caret past the existing "(" — otherwise we'd produce a doubled
    // parenthesis like AVERAGE((…).
    if (nextChar === '(') {
      return { text: label, caretOffset: label.length + 1 };
    }
    const text = `${label}(`;
    return { text, caretOffset: text.length };
  }
  // Otherwise strip any placeholder syntax and insert as-is.
  const text = snippet.replace(/\$\{\d+:?([^}]*)\}/g, '$1').replace(/\$\d+/g, '');
  return { text, caretOffset: text.length };
};
